<template>
    <div class="vehicle-block expanded">
        <div class="vehicle-header" v-if="titlePosition === 'top'">
            <h5
                :class="{
                    'with-drivego-icon': currentVehicle.is_drive_and_go,
                }"
            >
                <img :src="brandingImagesPath('/images/svgs/dg-icon.svg')" class="drivego-icon" v-if="currentVehicle.is_drive_and_go" />{{ currentVehicle.name }}
            </h5>
            <strong class="amount" v-if="currencyIsChanging">&hellip;</strong>
        </div>
        <div :class="['vehicle-img expanded', { 'row m-0': cardType === 'us-source-booking' }]">
            <div
                :class="['vehicle-img-wrapper expanded', { 'col-4 p-1': cardType === 'us-source-booking' }]"
                :style="cardType === 'us-source-booking' ? 'background-image:url(' + imageUrl(currentVehicle.image) + ')' : ''"
                v-if="currentVehicle.image"
            >
                <div class="vehicle-image-overlay d-block d-lg-none expanded"></div>
                <img class="vehicle-image vehicle-fixed-height expanded" :src="imageUrl(currentVehicle.image)" :alt="currentVehicle.name" @error="imageLoadFailed(currentVehicle.image)" />
                <img class="loyalty-medal" :src="userLoyalty.image" :alt="userLoyalty.name" v-if="loyaltyDiscountApplied" />
            </div>
            <div :class="['vehicle-header', { 'col-8 p-1': cardType === 'us-source-booking' }]" v-if="titlePosition !== 'top'">
                <h5
                    :class="{
                        'with-drivego-icon': currentVehicle.is_drive_and_go,
                    }"
                >
                    <img :src="brandingImagesPath('/images/svgs/dg-icon.svg')" class="drivego-icon" v-if="currentVehicle.is_drive_and_go" />{{ currentVehicle.name }}
                </h5>
                <strong class="amount" v-if="currencyIsChanging">&hellip;</strong>
                <strong class="amount" v-else-if="activeProduct && activeProduct.total_price > 0">{{ priceWithPaymentType(totals, quotePaymentType) }}</strong>
            </div>
        </div>

        <div :class="['vehicle-specs-icons expanded', { 'pt-2 pb-2': cardType === 'us-source-booking' }]">
            <div class="icon-spec">
                {{ vehicle.group }}
            </div>
            <div class="icon-spec" v-if="currentVehicle.refrigerated">
                <div class="vehicle-spec-item-icon">
                    <img :src="brandingImagesPath('/images/specs-icons/refrigerated-icon.svg')" alt="Refrigerated" />
                </div>
                <div class="vehicle-spec-item-description">
                    {{ labels.refrigerated }}
                </div>
            </div>
            <div class="icon-spec" v-if="currentVehicle.adults">
                <div class="vehicle-spec-item-icon">
                    <img :src="brandingImagesPath('/images/specs-icons/number-of-passengers-icon.svg')" />
                </div>
                <div class="vehicle-spec-item-description">
                    {{ currentVehicle.adults }}
                </div>
            </div>
            <div class="icon-spec" v-if="luggageLabel.short">
                <gm-tooltip :text="luggageLabel.long" click="enabled">
                    <div class="vehicle-spec-item-icon">
                        <img :src="brandingImagesPath('/images/specs-icons/number-of-bags-icon.svg')" alt="Number of bags" />
                    </div>
                </gm-tooltip>
                <div class="vehicle-spec-item-description">
                    {{ luggageLabel.short }}
                </div>
            </div>
            <div class="icon-spec" v-if="currentVehicle.fuel">
                <div class="vehicle-spec-item-icon">
                    <img :src="brandingImagesPath('/images/specs-icons/' + fuelType.icon)" alt="Fuel type" />
                </div>
                <div class="vehicle-spec-item-description">
                    {{ fuelType.label }}
                </div>
            </div>
            <div class="icon-spec" v-if="vehicle.co2 > 0">
                <div class="vehicle-spec-item-icon">
                    <img :src="brandingImagesPath('/images/specs-icons/co2-icon.svg')" alt="CO2" />
                </div>
                <div class="vehicle-spec-item-description">{{ currentVehicle.co2 }}<sub>2</sub></div>
            </div>
            <div class="icon-spec" v-if="currentVehicle.air_conditioning">
                <div class="vehicle-spec-item-icon">
                    <img :src="brandingImagesPath('/images/specs-icons/aircon-icon.svg')" alt="Air con" />
                </div>
                <div class="vehicle-spec-item-description">
                    {{ labels.airCon }}
                </div>
            </div>
            <div class="icon-spec" v-if="currentVehicle.transmission">
                <div class="vehicle-spec-item-icon">
                    <img :src="brandingImagesPath('/images/specs-icons/' + transmission.icon)" alt="Transmission" />
                </div>
                <div class="vehicle-spec-item-description">
                    {{ transmission.label }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ImageUrlFallback from '../../mixins/imageUrlFallback';
import Accounting from '../../mixins/accounting';
import Tooltip from '../shared/Tooltip';
import branding from '../../mixins/branding';
import ProductDetails from '../../lang/ProductDetails';
import ProductDetailsComponent from './ProductDetails';

export default {
    name: 'VehicleVisualSummary',
    mixins: [Accounting, ImageUrlFallback, branding],
    i18n: ProductDetails,
    props: {
        vehicle: Object,
        currentVehicle: Object,
        currencyIsChanging: Boolean,
        activeProduct: Object,
        labels: Object,
        experiment: String,
        titlePosition: {
            type: String,
            default: 'top',
        },
        cardType: {
            type: String,
            default: 'other-card',
        },
    },
    components: {
        'gm-tooltip': Tooltip,
        'product-details': ProductDetailsComponent,
    },
    computed: {

        fuelType() {
            let label = this.vehicle.fuel || '';

            if (this.vehicle.fuel === 'Petrol/Diesel') {
                label = this.labels.fuel;
            }

            label = label.toLowerCase();

            let translationFuel = this.$t('ProductDetails.spec_fuel_petrol');

            if (label === 'petrol' && translationFuel !== 'ProductDetails.spec_fuel_petrol') {
                label = this.$t('ProductDetails.spec_fuel_petrol');
            } else if (label === 'petrol/diesel' || label === 'diesel/petrol') {
                translationFuel = this.$t('ProductDetails.spec_fuel_petrol_diesel');
                if (translationFuel !== 'ProductDetails.spec_fuel_petrol_diesel') {
                    label = this.$t('ProductDetails.spec_fuel_petrol_diesel');
                }
            }

            let icon = 'fuel-type-icon.svg';

            if (['Hybrid', 'Electric'].indexOf(this.vehicle.fuel) > -1) {
                icon = 'battery-icon.svg';
            }

            if (['Hydrogen'].indexOf(this.vehicle.fuel) > -1) {
                icon = 'hydrogen-icon.svg';
            }

            return label
                ? {
                      label: label,
                      icon,
                  }
                : null;
        },
        loyaltyDiscountApplied() {
            return this.vehicle.loyalty_discount_applied && this.userLoyalty;
        },
        luggageLabel() {
            let luggageTypes = {
                short: [],
                long: [],
            };

            if (this.vehicle.luggage_large) {
                luggageTypes.short.push(this.vehicle.luggage_large + this.labels.luggage.large.short);
                luggageTypes.long.push(`${this.vehicle.luggage_large} ${this.labels.luggage.large.long}`);
            }

            if (this.vehicle.luggage_medium) {
                luggageTypes.short.push(this.vehicle.luggage_medium + this.labels.luggage.medium.short);
                luggageTypes.long.push(`${this.vehicle.luggage_medium} ${this.labels.luggage.medium.long}`);
            }

            if (this.vehicle.luggage_small) {
                luggageTypes.short.push(this.vehicle.luggage_small + this.labels.luggage.small.short);
                luggageTypes.long.push(`${this.vehicle.luggage_small} ${this.labels.luggage.small.long}`);
            }

            return {
                short: luggageTypes.short.join(', '),
                long: luggageTypes.long.join(', '),
            };
        },

        transmission() {
            let label = this.vehicle.transmission || '';
            let icon = 'transmission-manual-icon.svg';
            if (label.length === 0) {
                //no-op
            } else if (label.toLowerCase().indexOf('manual') > -1) {
                label = this.labels.transmission.manual;
            } else {
                label = this.labels.transmission.automatic;
                icon = 'transmission-auto-icon.svg';
            }

            return {
                label: label.toLowerCase(),
                icon,
            };
        },
        totals() {
            return this.$store.getters.getTotals;
        },
        quotePaymentType() {
            return this.$store.getters.quotePaymentType;
        },
        ...mapGetters({
            selectedCurrency: 'selectedCurrency',
            user: 'getQuoteUser',
        }),
    },
    data() {
        return {
            imageErrorUrls: [],
            url: null,
        };
    },
};
</script>

<style lang="scss" src="./scss/vehicle-visual-summary.scss"></style>
